import type {
  IBulkUpdateMinersForm,
  IMinerPartAllocationForm,
  IMinerPartDeleteForm,
  IMinerPartMoveToInventoryForm,
} from '@/types/rc/forms'
import type { IConsolidationAction, IPartDamageCreationForm, IPartDamageDeletionForm } from '@/types/rc/miners/forms'
import axios from 'axios'

/**
 * Delete a part from a miner
 *
 * @param {number} id miner ID
 * @param {IMinerPartDeleteForm} data part data
 * @throws {AxiosError}
 */
export async function deleteMinerPart(id: number, data: IMinerPartDeleteForm) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/parts`, { data })
  return res.data
}

/**
 * Move part to inventory
 *
 * @param {number} id miner ID
 * @param {IMinerPartMoveToInventoryForm} data part data
 * @throws {AxiosError}
 */
export async function movePartToInventory(id: number, data: IMinerPartMoveToInventoryForm) {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/parts/movement`, { ...data })
  return res.data
}

/**
 * Move part back from inventory
 *
 * @param {number} id miner ID
 * @param {IMinerPartMoveToInventoryForm} data part data
 * @throws {AxiosError}
 */
export async function movePartBackFromInventory(id: number, data: IMinerPartMoveToInventoryForm) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/parts/movement`, {
    data: { ...data },
  })
  return res.data
}

/**
 * Allocate a spare part to a miner
 *
 * @param {number} id miner ID
 * @param {IMinerPartAllocationForm} data part data
 * @throws {AxiosError}
 */
export async function allocatePartToMiner(id: number, data: IMinerPartAllocationForm) {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}/parts/allocation`, { ...data })
  return res.data
}

/**
 * Delete a miner
 *
 * @param {number} id miner ID
 * @throws {AxiosError}
 */
export async function deleteMiner(id: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${id}`)
  return res.data
}

/**
 * Move parts between miners
 *
 * @param {IPartsMoveForm} consolidations list of consolidations, each containing the part type and the miners involved
 * @throws {AxiosError}
 */
export async function consolidateParts(consolidations: IConsolidationAction[]) {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/parts/consolidation`, { consolidations })
  return res.data
}

/**
 * Bulk update miners
 *
 * @param {IBulkUpdateMinersForm} data bulk miners update form
 * @throws {AxiosError}
 */
export async function bulkUpdateMiners(data: IBulkUpdateMinersForm) {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/bulk`, { ...data })
  return res.data
}

/**
 * Delete a miner's diagnostic
 *
 * @param minerId the miner id
 * @throws {AxiosError}
 */
export async function deleteMinerDiagnostic(minerId: number) {
  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/diagnostic`)
  return res.data
}

/**
 * Creates a damage on a part
 *
 * @param minerId the miner id
 * @throws {AxiosError}
 */
export async function createPartDamage(minerId: number, form: IPartDamageCreationForm) {
  const formData = new FormData()

  formData.append('part_id', form.part_id.toString())
  formData.append('part_type', form.part_type)
  formData.append('damage_type', form.damage_type)
  formData.append('description', form.description)

  form.pictures.forEach((pic, i) => formData.append(`pictures[${i}]`, pic))

  const res = await axios.post(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/parts/damages`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return res.data
}

/**
 * Deletes a damage on a part
 *
 * @param minerId the miner id
 * @throws {AxiosError}
 */
export async function deletePartDamage(minerId: number, data: IPartDamageDeletionForm) {
  const formData = new FormData()

  formData.append('part_id', data.part_id.toString())
  formData.append('part_type', data.part_type)
  formData.append('damage_id', data.damage_id.toString())

  const res = await axios.delete(`${import.meta.env.VITE_APP_RC_MINERS_ENDPOINT}/${minerId}/parts/damages`, {
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}
